import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { Autocomplete } from '@mui/material';
import React, { useEffect, useReducer, useState } from 'react';
import { Company, Department } from '../../api/api-generated';
import {
  useFindAllCompaniesQuery,
  useFindAlldepartmentsQuery,
  useFindOneInventoryHardwareQuery,
  useGetAllTypesQuery,
  useRemoveInventoryHardwareMutation,
} from '../../api/api-hooks';
import { useAnalyzerSerialAutocomplete } from '../../hooks/useAnalyzerSerialAutocomplete';
import {
  getISOStringOneYearFromToday,
  truncateISOString,
} from '../../utils/dateTime';
import { nullStringCheck } from '../../utils/stringManipulation';
import DepartmentsButton from '../departments/DepartmentsButton';
import SoftwareSoldCreatorWithDefaults from '../software-sold/SoftwareSoldCreatorWithDefaults';
import { HardwareItemForm } from './hardwareTypes';
import {
  hardwareHandlerMiddleware,
  hardwareTransferValidator,
} from './hardwareUtils';
import useHardwareItemCreator from './useHardwareItemCreator';

interface Props {
  dialogOpen: boolean;
  setDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  analyzerId: string | number;
}

const addFormInitialState: HardwareItemForm = {
  hardwareType: null,
  companies: [],
  firmware: undefined,
  notes: undefined,
  serial: '',
  shipper: undefined,
  warrantyStartDate: truncateISOString(new Date().toISOString()),
  warrantyEndDate: truncateISOString(getISOStringOneYearFromToday()),
  trackingNumber: undefined,
  order: null,
  lptSold: 'true',
  manufacturer: 'LP Technologies',
  inventoryHardware: null,
  department: null,
};

const HardwareCreatorFromInventory = ({
  dialogOpen,
  setDialogOpen,
  analyzerId,
}: Props) => {
  const { data: companies } = useFindAllCompaniesQuery({});
  const { data: hardwareTypes } = useGetAllTypesQuery({});
  const { data: departments } = useFindAlldepartmentsQuery({});
  const [deleteInventoryHardware] = useRemoveInventoryHardwareMutation();
  const { data: inventoryHardware } = useFindOneInventoryHardwareQuery({
    id: Number(analyzerId),
  });
  const [softwareDialogOpen, setSoftwareDialogOpen] = useState<boolean>(false);
  const [addFormState, setAddFormState] = useReducer(
    (curVals: any, newVals: any) => ({ ...curVals, ...newVals }),
    addFormInitialState
  );
  const [hardwareSubmitted, setHardwareSubmitted] = useState(false);
  const [hardwareResults, setHardwareResults] = useState<string>('');
  const hardwareCreator = useHardwareItemCreator();
  const analyzerAutoComplete = useAnalyzerSerialAutocomplete();

  // Refresh form initial state whenever analyzerId or the pulled InventoryHardware changes
  //  (i.e., is clicked)
  useEffect(() => {
    if (inventoryHardware === undefined || !analyzerId) return;
    setAddFormState({
      ...addFormInitialState,
      hardwareType: inventoryHardware.hardwareType,
      inventoryHardware: inventoryHardware,
    });
  }, [analyzerId, inventoryHardware]);

  if (
    companies === undefined ||
    hardwareTypes === undefined ||
    inventoryHardware === undefined ||
    departments === undefined
  )
    return null;

  const handleHardwareSubmit = async () => {
    setHardwareSubmitted(true);
    const creationResult = await hardwareCreator(addFormState);
    if (creationResult === undefined) {
      setHardwareResults('Failed to create hardware.');
    } else if ('error' in creationResult) {
      setHardwareResults(
        // @ts-ignore
        `Failed to transfer hardware. ${creationResult.error.data.message}`
      );
    } else {
      deleteInventoryHardware({ id: Number(analyzerId) });
      setHardwareResults(
        'Hardware successfully transferred from inventory to hardware sold. Would you like to add associated software to software sold?'
      );
    }
  };
  const handler = (
    event: any,
    autoboxValue?: object | null,
    autoboxLabel?: string
  ) => {
    const { name, value } = event.target;
    const middlewareResult = hardwareHandlerMiddleware(
      name,
      value,
      addFormState,
      autoboxValue,
      autoboxLabel
    );
    const autoCompleteResults = analyzerAutoComplete(name, value);
    setAddFormState({
      [name]: value,
      ...autoCompleteResults,
      ...middlewareResult,
    });
  };
  const handleCancel = () => {
    setDialogOpen(false);
    setHardwareSubmitted(false);
  };
  const handleOpenSoftware = () => {
    setSoftwareDialogOpen(true);
    setHardwareSubmitted(false);
    setDialogOpen(false);
  };

  return (
    <>
      <Dialog
        fullWidth
        maxWidth='md'
        open={dialogOpen}
        aria-labelledby='form-dialog-title'
      >
        <DialogTitle id='form-dialog-title'>Mark Hardware Sold</DialogTitle>
        {!hardwareSubmitted && (
          <>
            <DialogContent>
              <Grid container direction='column' spacing={1}>
                <Grid item>
                  <Typography>
                    Serial:{' '}
                    {addFormState.inventoryHardware?.serial
                      ? addFormState.inventoryHardware.serial
                      : 'None selected'}
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography>
                    Hardware Type:{' '}
                    {addFormState.hardwareType
                      ? addFormState.hardwareType.typeName
                      : 'None selected'}
                  </Typography>
                </Grid>

                {addFormState.companies.length > 0 ? (
                  addFormState.companies.map(
                    (company: Company, idx: number) => (
                      <Grid key={idx} item>
                        <Autocomplete
                          onChange={(event, value) => {
                            handler(event, value, `companies${idx}`);
                          }}
                          value={company}
                          options={companies}
                          getOptionLabel={(company) => company.companyName}
                          style={{ width: 300 }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label='Select Company'
                              variant='outlined'
                            />
                          )}
                        />
                      </Grid>
                    )
                  )
                ) : (
                  <Grid item>
                    <Autocomplete
                      onChange={(event, value) => {
                        handler(event, value, 'companies0');
                      }}
                      value={addFormState.companies[0]}
                      options={companies}
                      getOptionLabel={(company) => company.companyName}
                      style={{ width: 300 }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label='Select Company'
                          variant='outlined'
                        />
                      )}
                    />
                  </Grid>
                )}
                <Grid item container alignItems='center' spacing={1}>
                  <Grid item>
                    <Autocomplete
                      onChange={(event, value) => {
                        handler(event, value, 'department');
                      }}
                      value={addFormState.department}
                      options={departments.filter((department: Department) =>
                        addFormState.companies.some(
                          (company: Company) =>
                            company?.id === department?.companyId
                        )
                      )}
                      getOptionLabel={(department) => department.departmentName}
                      style={{ width: 300 }}
                      disabled={
                        !addFormState.companies ||
                        addFormState.companies.length < 1 ||
                        addFormState.companies.every(
                          (entry: any) => entry === null
                        )
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label='Select Department'
                          variant='outlined'
                        />
                      )}
                    />
                  </Grid>
                  <Grid item>
                    <DepartmentsButton company={addFormState.companies[0]} />
                  </Grid>
                </Grid>
                <Grid item>
                  <TextField
                    margin='dense'
                    name='firmware'
                    label='Firmware'
                    fullWidth
                    value={nullStringCheck(addFormState.firmware)}
                    onChange={handler}
                  />
                </Grid>
                <Grid item>
                  <TextField
                    margin='dense'
                    name='manufacturer'
                    label='Manufacturer'
                    fullWidth
                    value={nullStringCheck(addFormState.manufacturer)}
                    onChange={handler}
                  />
                </Grid>
                <Grid item>
                  <Grid container alignItems='center' spacing={1}>
                    <Grid item>
                      <Typography>Warranty Start: </Typography>
                    </Grid>
                    <Grid item>
                      <TextField
                        type='date'
                        margin='dense'
                        name='warrantyStartDate'
                        value={truncateISOString(
                          nullStringCheck(addFormState.warrantyStartDate)
                        )}
                        onChange={handler}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item>
                  <Grid container alignItems='center' spacing={1}>
                    <Grid item>
                      <Typography>Warranty End: </Typography>
                    </Grid>
                    <Grid item>
                      <TextField
                        type='date'
                        margin='dense'
                        name='warrantyEndDate'
                        value={truncateISOString(
                          nullStringCheck(addFormState.warrantyEndDate)
                        )}
                        onChange={handler}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item>
                  <TextField
                    margin='dense'
                    name='trackingNumber'
                    label='Tracking #'
                    fullWidth
                    value={nullStringCheck(addFormState.trackingNumber)}
                    onChange={handler}
                  />
                </Grid>
                <Grid item>
                  <TextField
                    margin='dense'
                    name='shipper'
                    label='Shipper'
                    fullWidth
                    value={nullStringCheck(addFormState.shipper)}
                    onChange={handler}
                  />
                </Grid>
              </Grid>
              {!hardwareTransferValidator(addFormState) && (
                <Typography color='error'>
                  Company and Firmware are required.
                </Typography>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCancel} color='primary'>
                Cancel
              </Button>
              {hardwareTransferValidator(addFormState) && (
                <Button onClick={handleHardwareSubmit} color='primary'>
                  Mark Hardware Sold
                </Button>
              )}
            </DialogActions>
          </>
        )}
        {hardwareSubmitted && (
          <>
            <DialogContent>
              <Typography>{hardwareResults}</Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleOpenSoftware} color='primary'>
                Add Software
              </Button>
              <Button onClick={handleCancel} color='primary'>
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <SoftwareSoldCreatorWithDefaults
        company={addFormState.companies[0]}
        serial={addFormState.serial}
        dialogOpen={softwareDialogOpen}
        setDialogOpen={setSoftwareDialogOpen}
      />
    </>
  );
};

export default HardwareCreatorFromInventory;
